import React from 'react';
import PropTypes from 'prop-types';
import * as immutable from 'immutable';
import {List, Map} from 'immutable';
import Loading from 'components/pure/utils/Loading';
import Moment from 'moment';
import {
  fetchTeamStats,
  TOPIC_ROLLUP_SUBTABS,
  TOPIC_ROLLUP_SUMMARY_TEAM,
} from 'components/dashboard/rollups/topic/common';
import Tabs from 'components/pure/utils/Tabs';
import round from 'utils/round';
import mapValues from 'lodash/mapValues';
import StatTab from 'components/dashboard/StatTab';
import TopicRollupSummaryTab from 'components/dashboard/rollups/topic/TopicRollupSummaryTab';
import TopicRollupTeamTab from 'components/dashboard/rollups/topic/TopicRollupTeamTab';
import {setStateFromPromise} from 'components/dashboard/rollups/topic/withLoading';
import Team from 'records/Team';
import {browserHistory} from 'react-router';
import {allTeam} from 'components/dashboard/DashboardBreadcrumbs';
import AuthStore from 'stores/AuthStore';
import DashMetricsActions from 'actions/dashboard/DashMetricsActions';
import {HierarchyType} from 'sources/dashboard/DashMetricsSource';
import connectToStores from 'alt-utils/lib/connectToStores';
import DashMetricsStore from 'stores/dashboard/DashMetricsStore';
import LoadingOverlay from 'components/pure/utils/LoadingOverlay';
import CallActions from 'actions/CallActions';
import {getStringQueryParam} from 'utils/getQueryParam';
import {shouldUpdate} from 'components/StatsPage2.utils';
import {callDrillDown} from 'components/dashboard/rollups/topic/CallDrillDown';

const MAX_TABS = 5;
const MAX_LABEL_LENGTH = 23;
const MAX_TOTAL_LABEL_LENGTH = 120;

function formatNumberOrEmptyString(number) {
  // Tabs don't want to show any summary numbers so we return an empty string here
  if (typeof number !== 'number') {
    return '';
  }

  // avoid showing decimals for whole numbers, otherwise show 2 digits
  if (number % 1 > 0) {
    return number.toFixed(2);
  } else {
    return number;
  }
}

function formatPercent(percent) {
  return percent && (Number.isNaN(percent) ? 'N/A' : `${round(percent)}%`);
}

/**
 * Convert tab items to a tab config.
 *
 * @param tabItems {List<{id: string, label: string}>} items to turn into tab configs.
 * @param triggerNameById {function(string|number): string} function to get a trigger name by its id,
 * which, though a number, can also be passed as a string.
 *
 * @return tab configs for the given items.
 */
function toTabConfigs(tabItems, triggerNameById) {
  const tabs = {};
  for (const tabItem of tabItems) {
    tabs[tabItem.id] = toTabConfig(tabItem, triggerNameById);
  }
  return tabs;
}

/**
 * Convert a single tab item to a tab config.
 */
function toTabConfig(tabItem, triggerNameById) {
  const percentDescription = topic => `Percentage of calls where rep discussed ${topic}`;
  const countDescription = topic => `Count of calls where rep discussed ${topic}`;

  // Give each department a count and percentage subtab.
  const subtabs = {};
  const chartTitle = tabItem.label;
  subtabs[TOPIC_ROLLUP_SUBTABS.PERCENT] = {
    isPercent: true, // Used by drill downs.
    label: 'Percentage',
    chartTitle,
    description: percentDescription(tabItem.id === TOPIC_ROLLUP_SUMMARY_TEAM.id ? 'topic' : ''),
    drillDownDescriptionFn: id => percentDescription(`"${triggerNameById(id)}"`),
    formatValue: formatPercent,
  };
  subtabs[TOPIC_ROLLUP_SUBTABS.COUNT] = {
    isPercent: false, // Used by drill downs.
    label: 'Count',
    chartTitle,
    description: countDescription(tabItem.id === TOPIC_ROLLUP_SUMMARY_TEAM.id ? 'topic' : ''),
    drillDownDescriptionFn: id => countDescription(`"${triggerNameById(id)}"`),
    formatValue: formatNumberOrEmptyString,
  };
  return {
    label: tabItem.label,
    subtabs,
    description: '',
    formatValue: formatNumberOrEmptyString,
    summary: _ => null,
  };
}

/**
 * Convert teams to tab items (having an id and a label), and split these into two lists, one to
 * render as actual tabs and another to render in the dropdown.
 *
 * @param teams {List<Team>} teams.
 * @returns {{dropdownTeams, tabTeams}}
 */
function splitTeamsIntoInitialTabsAndDropdowns(teams) {
  // TODO can we combine this with the logic in SAWScorecardTabs.heuristicTopN? That one takes an
  // array, this one a List, but the logic is otherwise almost identical.

  const tabItems = teams
    .sortBy(team => team.name)
    .splice(0, 0, TOPIC_ROLLUP_SUMMARY_TEAM)
    .map(team => ({id: team.id, label: team.name}))
    .update(teams.size, tabItem => (teams.size === 1 ? {...tabItem, label: 'By User'} : tabItem));

  const len = tabItems.size;
  let labelsTotalLength = 0;
  let tabCount = 0;
  for (let i = 0; i < len; i++) {
    const tabItem = tabItems.get(i);
    tabCount++;
    labelsTotalLength += Math.min(tabItem.label.length, MAX_LABEL_LENGTH);
    if (tabCount >= MAX_TABS || labelsTotalLength >= MAX_TOTAL_LABEL_LENGTH) {
      break;
    }
  }
  if (tabCount === len - 1) {
    // the drop-down will only have one item, so may as well show it as tab
    tabCount = len;
  }
  const tab = tabItems.slice(0, tabCount);
  const dropdown = tabItems.slice(tabCount, len);

  return {tabItems: tab, dropdownItems: dropdown};
}

function tabIdFromDashboardSelectedTeam(selectedTeamId) {
  // we default to summary
  if (selectedTeamId === TOPIC_ROLLUP_SUMMARY_TEAM.id || !selectedTeamId) {
    return TOPIC_ROLLUP_SUMMARY_TEAM.id;
  }

  return selectedTeamId;
}

function selectedTeamFromTabId(tabId, dashboardManagerId, teams) {
  if (tabId === TOPIC_ROLLUP_SUMMARY_TEAM.id) {
    return allTeam(dashboardManagerId, AuthStore.getState().user.id);
  } else {
    return teams.find(team => team.id === tabId);
  }
}

/**
 * Roll up by topics.
 *
 * This component is responsible for managing the rollup's tabs (specifically, which is selected),
 * and rendering the appropriate component.
 */
@connectToStores
export default class TopicRollup extends React.PureComponent {
  static propTypes = {
    // Supplied by dashboard (see StatsPage2)
    adminId: PropTypes.string,
    // Ditto.
    coachId: PropTypes.string,
    // Ditto.
    endDate: PropTypes.instanceOf(Moment).isRequired,
    loading: PropTypes.bool,
    loadingError: PropTypes.bool,
    location: PropTypes.object.isRequired, // React router location.
    // Map<string, {departmentNames: List<string>, teamNames: List<string}>
    profileUsageByTriggerRuleId: PropTypes.instanceOf(immutable.Map),
    // Also supplied by dashboard.
    selectedTeam: PropTypes.instanceOf(Team),
    // Also supplied by dashboard.
    startDate: PropTypes.instanceOf(Moment).isRequired,
    summaryStats: PropTypes.instanceOf(immutable.Map), // Map<string, Metric> (keys are TeamIds)
    teams: PropTypes.instanceOf(immutable.List), // List<Team>
    // Map<number, string> (trigger rule id to name)
    triggerIdToName: PropTypes.instanceOf(immutable.Map),
  };

  // Defaults for props not available during initial render(s).
  static defaultProps = {
    loading: true,
    loadingError: false,
    profileUsageByTriggerRuleId: Map(),
    summaryStats: Map(),
    teams: List(),
    triggerIdToName: Map(),
  };

  constructor(props) {
    super(props);
    const activeTabId = TOPIC_ROLLUP_SUMMARY_TEAM.id;
    this.state = {
      activeTabId,
      activeSubtabId: TOPIC_ROLLUP_SUBTABS.PERCENT,
      initialized: false,
      dropdownItems: List(),
      tabItems: List(),
      switchingTabs: false,
    };
  }

  static getStores() {
    return [DashMetricsStore];
  }

  static getPropsFromStores() {
    const {loadingCallSearch} = DashMetricsStore.getState();

    return {
      loadingCallSearch,
    };
  }

  componentDidMount() {
    DashMetricsActions.clearTopicIds.defer();
    CallActions.clearDrilldown.defer();
  }

  isPercent() {
    const {activeSubtabId} = this.state;
    return activeSubtabId === TOPIC_ROLLUP_SUBTABS.PERCENT;
  }

  triggerNameById = id => {
    const {triggerIdToName} = this.props;
    // context is a string, but map keys are numbers, so it is sometimes necessary to convert.
    return triggerIdToName.get(Number(id));
  };

  swapTabsAndDropdownsIfNeeded(newActiveTabId) {
    const {activeTabId, tabItems, dropdownItems} = this.state;

    // Keep the user on their count/percent selection when they switch tabs.
    const newSubtabId = this.isPercent()
      ? TOPIC_ROLLUP_SUBTABS.PERCENT
      : TOPIC_ROLLUP_SUBTABS.COUNT;

    // TODO can we combine tab swapping logic here with SAWScorecardTabs.scorecardOptionChange?
    //  Tabs there are arrays, and ours are lists.

    // Swap dropdown selection into first available tab after 'Summary'.
    const idxInDropdown = dropdownItems.map(t => t.id).indexOf(newActiveTabId);

    let newTabItems;
    let newDropdownItems;
    if (idxInDropdown >= 0) {
      const currentTabIdx = tabItems.map(t => t.id).indexOf(activeTabId);
      // Avoid swapping out 'Summary'.
      const tabToSwapIdx = currentTabIdx === 0 ? 1 : currentTabIdx;
      newTabItems = tabItems.splice(tabToSwapIdx, 1, dropdownItems.get(idxInDropdown));
      newDropdownItems = dropdownItems.splice(idxInDropdown, 1, tabItems.get(tabToSwapIdx));
    } else {
      // No change to tabs.
      newTabItems = tabItems;
      newDropdownItems = dropdownItems;
    }

    return {
      activeTabId: newActiveTabId,
      activeSubtabId: newSubtabId,
      tabItems: newTabItems,
      dropdownItems: newDropdownItems,
    };
  }

  switchToTab = newActiveTabId => {
    this.setState({switchingTabs: true});

    const loadTeamDataPromiseProducer = props => {
      const {startDate, endDate, triggerIdToName} = props;
      return fetchTeamStats({
        startDate,
        endDate,
        teamId: newActiveTabId,
        triggerIdToName,
        includePercentages: true,
      });
    };

    const selectedTeam = selectedTeamFromTabId(
      newActiveTabId,
      this.props.managerId,
      this.props.teams
    );
    browserHistory.replace({
      pathname: this.props.location.pathname,
      // this needs set to satisfy StatsPage2
      state: {
        selectedTeam,
      },
      query: {
        // but we do THIS so we can come back from the drilldown
        selectedTeamId: selectedTeam.id || TOPIC_ROLLUP_SUMMARY_TEAM.id,
        triggerId: getStringQueryParam(this.props.location, 'triggerId'),
      },
    });

    const initialState = {
      teamStatsError: false,
      teamStatsLoading: true,
      ...this.swapTabsAndDropdownsIfNeeded(newActiveTabId),
    };

    setStateFromPromise(
      this,
      loadTeamDataPromiseProducer,
      initialState,
      ([teamStats, teamStatsUsers]) => ({
        teamStats,
        teamStatsLoading: false,
        teamStatsUsers,
        switchingTabs: false,
      }),
      e => {
        console.error(e);
        return {teamStatsError: true};
      }
    );
  };

  selectSubtab = activeSubtabId => {
    this.setState({
      activeSubtabId,
    });
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    const {tabItems, switchingTabs} = this.state;
    const {loading, teams, selectedTeam: selectedTeamFromProps} = this.props;
    if (!loading && !switchingTabs) {
      // Loading's done! We can now set up derived state.
      // We should be doing this in the new lifecycle method once we upgrade react...

      // Only set the tabs up once (or at least avoid stomping on them just because props have
      // changed).
      if (tabItems.isEmpty()) {
        return this.setState(splitTeamsIntoInitialTabsAndDropdowns(teams));
      }

      // ah, teamid.
      // if there's a query param team id - prefer that most of the time, unless
      // the props selected team JUST changed, in which case we use that.
      // props selected team is the header widget.
      // query param team is set when switching from tab to tab

      const selectedTeamId = getStringQueryParam(this.props.location, 'selectedTeamId');

      const oldSelectedTeamId = prevProps.selectedTeam && prevProps.selectedTeam.id;
      const selectedTeamFromPropsDidMeaningfullyChange =
        oldSelectedTeamId && oldSelectedTeamId !== selectedTeamFromProps;

      const teamIdToUse = selectedTeamFromPropsDidMeaningfullyChange
        ? selectedTeamFromProps && selectedTeamFromProps.id
        : selectedTeamId || selectedTeamFromProps;

      const tabId = tabIdFromDashboardSelectedTeam(teamIdToUse);

      // Adjust active tab based on selected team, if any.
      // NOTE: this selectedTeam is NOT the normal dashboard selectedTeam

      if (selectedTeamId && tabId !== prevState.activeTabId) {
        return this.switchToTab(tabId);
      }

      if (shouldUpdate(prevProps, this.props, false, true, false)) {
        return this.switchToTab(tabId);
      }
    }
  }

  render() {
    const {
      endDate,
      loading,
      loadingError,
      location,
      profileUsageByTriggerRuleId,
      summaryStats,
      startDate,
      teams,
      loadingCallSearch,
    } = this.props;
    const {
      activeTabId,
      activeSubtabId,
      dropdownItems,
      tabItems,
      teamStats,
      teamStatsError,
      teamStatsLoading,
      teamStatsUsers,
    } = this.state;

    if (loading || this.state.tabItems.isEmpty()) {
      return <Loading />;
    }
    if (loadingError) {
      return <div>Problem loading the data.</div>;
    }

    const tabConfigs = toTabConfigs(tabItems, this.triggerNameById);
    const tabRenderers = mapValues(tabConfigs, tabConfig => tab => (
      <StatTab tabConfig={tabConfig} value={tab.value} maxLabelLength={MAX_LABEL_LENGTH} />
    ));

    const activeTabConfig = tabConfigs[activeTabId];
    const activeSubtabConfig = activeTabConfig.subtabs[activeSubtabId];
    const isSummary = activeTabId === TOPIC_ROLLUP_SUMMARY_TEAM.id;

    return (
      <div className="dashv2-widget topic-rollup">
        {loadingCallSearch && <LoadingOverlay />}
        <Tabs
          onUpdateTab={this.switchToTab}
          activeTabId={activeTabId}
          tabRenderers={tabRenderers}
          dropdowns={dropdownItems}
        />
        {isSummary ? (
          <TopicRollupSummaryTab
            activeSubtabConfig={activeSubtabConfig}
            activeSubtabId={activeSubtabId}
            adminId={this.props.adminId}
            coachId={this.props.coachId}
            conversations={this.props.conversations}
            endDate={endDate}
            headerStartDate={this.props.headerStartDate}
            headerEndDate={this.props.headerEndDate}
            location={location}
            profileUsageByTriggerRuleId={profileUsageByTriggerRuleId}
            summaryStats={summaryStats}
            onSelectSubtab={this.selectSubtab}
            startDate={startDate}
            tabConfigs={tabConfigs}
            teams={teams}
            triggerIdToName={this.props.triggerIdToName}
            triggerNameFromStat={stat => this.triggerNameById(stat.context)}
            callDrillDown={callDrillDown({
              startDate,
              endDate,
              hierarchy: HierarchyType.team,
              ids: teams.map(t => t.id),
            })}
          />
        ) : (
          <TopicRollupTeamTab
            activeSubtabConfig={activeSubtabConfig}
            activeSubtabId={activeSubtabId}
            activeTabId={activeTabId}
            endDate={endDate}
            error={teamStatsError}
            headerStartDate={this.props.headerStartDate}
            headerEndDate={this.props.headerEndDate}
            loading={teamStatsLoading}
            location={location}
            onSelectSubtab={this.selectSubtab}
            startDate={startDate}
            stats={teamStats}
            tabConfigs={tabConfigs}
            triggerNameById={this.triggerNameById}
            users={teamStatsUsers}
            triggerId={getStringQueryParam(location, 'triggerId')}
            callDrillDown={callDrillDown({
              startDate,
              endDate,
            })}
          />
        )}
      </div>
    );
  }
}
